import React, {useRef, useState, useCallback} from "react";
import {ViewerItemsSelectionContext, ViewerItemsSelectionContextProps} from "./viewer-items-selection-context-props.ts";
import FormattedObject from "@/models/interfaces/viewer/formatted-object.ts";
import ViewerItemsSelectionChangedModel from "./models/viewer-items-selection-changed-model.ts";

const ViewerItemsSelectionProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    
    const [selectedItems, setSelectedItems] = useState<FormattedObject[]>([]);
    const selectionChangedListenersRef = useRef<Set<(event : ViewerItemsSelectionChangedModel) => void>>(new Set());
    
    const notifySelectionChangedListeners = useCallback((event : ViewerItemsSelectionChangedModel) => {
        selectionChangedListenersRef.current.forEach(listener => listener(event));
    }, []);
    
    const subscribeSelectionChanged = useCallback((listener : (event : ViewerItemsSelectionChangedModel) => void) => {
        selectionChangedListenersRef.current.add(listener);
        
        return () => {
            selectionChangedListenersRef.current.delete(listener);
        }
    }, []);
    
    const addItem = useCallback((item : FormattedObject) => {
        setSelectedItems(prev => {
            if(prev.includes(item))
                return prev;
            
            return [...prev, item];
        });
        notifySelectionChangedListeners({action: "add", item});
    }, [notifySelectionChangedListeners]);
    
    const removeItem = useCallback((item : FormattedObject) => {
        setSelectedItems(prev => prev.filter(i => i.workspaceUid !== item.workspaceUid && i.objectUid === item.objectUid));
        notifySelectionChangedListeners({action: "remove", item});
    }, [notifySelectionChangedListeners]);
    
    const clearSelection = useCallback(() => {
        setSelectedItems([]);
        notifySelectionChangedListeners({action: "clear"});
    }, [notifySelectionChangedListeners]);

    const contextProps: ViewerItemsSelectionContextProps = {
        selectedItems,
        subscribeSelectionChanged,
        
        addItem,
        removeItem,
        clearSelection
    }

    return (
        <ViewerItemsSelectionContext.Provider value={contextProps}>
            {children}
        </ViewerItemsSelectionContext.Provider>
    );
}

export default ViewerItemsSelectionProvider;